import React, { useEffect, useMemo, useState } from 'react';
import {
  Callout, ColumnPicker, ConfirmDialog, DataTable, DensityToggle, EmptyState, Field, FilterBar,
  FormRow, ImageDropzone, NumberField, Pagination, SearchInput, SideDrawer, SummaryStrip, Toolbar,
  exportFilename, exportRows, formatThaiDate, ghostBtnCls, inputCls, primaryBtnCls,
  secondaryBtnCls, useTableDensity, useToast,
  type ActiveFilter, type Column, type ImageValue, type SortState, type SummaryStat,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  BATCH_DEFAULTS, COUPONS, COUPON_KIND_LABEL, COUPON_STATUS_LABEL, STACK_RULE_LABEL,
  type Coupon, type CouponKind, type CouponStatus, type StackRule,
} from './loyaltyData';

/**
 * CouponScreen — คูปองและโค้ดส่วนลด (FR-ADM-20, FR-ADM-21)
 *
 * FR-ADM-20 ระบุช่องที่ต้องมีไว้ครบแล้ว: ประเภท · ยอดขั้นต่ำ · วันหมดอายุ ·
 * จำนวนสิทธิ์ทั้งหมด · จำนวนสิทธิ์ต่อคน · เงื่อนไขการใช้ร่วมกับส่วนลดอื่น
 * งานออกแบบจึงไม่ใช่การเลือกว่าจะมีช่องอะไร แต่คือการทำให้กรอกผิดยาก
 *
 * สามจุดที่กรอกผิดแล้วเสียหายจริง:
 *
 *   1. **ส่วนลดเปอร์เซ็นต์ที่ไม่มีเพดาน** — "ลด 15%" ดูปลอดภัยจนกว่าจะมีคนสั่ง 80,000 บาท
 *      หน้าจอจึงบังคับให้กรอกเพดานส่วนลดเมื่อเลือกประเภทเปอร์เซ็นต์
 *      และแสดงส่วนลดสูงสุดที่เป็นไปได้เป็นจำนวนเงินตลอดเวลา
 *
 *   2. **เงื่อนไขการใช้ร่วมกับส่วนลดอื่น** — คำถามที่ยังค้างกับทีม backend ในภาคผนวก SRS ยังไม่ได้คำตอบ
 *      ค่าตั้งต้นจึงเป็น "ใช้ร่วมกับส่วนลดอื่นไม่ได้" ซึ่งเป็นค่าที่ปลอดภัยที่สุด
 *      และหน้าจอบอกตรง ๆ ว่านี่เป็นค่าที่ตั้งไว้เพราะยังไม่มีนโยบาย ไม่ใช่เพราะตัดสินใจแล้ว
 *
 *   3. **การสร้างโค้ดแบบชุด (FR-ADM-21)** — สร้างแล้วแจกออกไปแล้วเรียกคืนไม่ได้
 *      จึงมีขั้นพรีวิวโค้ดตัวอย่างและตัวเลขจำนวนที่จะสร้างก่อนกดยืนยัน
 *      ชุดตัวอักษรตัด 0 O 1 I L ออก เพราะคนพิมพ์ผิดจากโค้ดที่พิมพ์บนกระดาษ
 */

type ScreenState = 'ready' | 'loading' | 'error';

/**
 * ⚠️ ยังไม่มีข้อกำหนดจากงานออกแบบ — ใช้ชุดเดียวกับของรางวัล (1:1)
 *    เพราะคูปองแสดงเป็นการ์ดสี่เหลี่ยมจัตุรัสในกระเป๋าคูปองของแอป
 *    ต้องยืนยันกับทีมออกแบบก่อนใช้งานจริง
 */
const IMAGE_SPEC = {
  aspect: '1:1' as const,
  minWidth: 800,
  maxKB: 300,
  usedAt: 'การ์ดคูปองในกระเป๋าคูปองของแอป',
};

const valueText = (c: Coupon): string => {
  if (c.kind === 'freeship') return 'ฟรีค่าจัดส่ง';
  if (c.kind === 'percent') return `${c.value}%${c.maxDiscount !== null ? ` (สูงสุด ${c.maxDiscount.toLocaleString('th-TH')} บาท)` : ''}`;
  return `${(c.value ?? 0).toLocaleString('th-TH')} บาท`;
};

const STATUS_STYLE: Record<CouponStatus, { bg: string; fg: string; icon: string }> = {
  draft: { bg: 'var(--surface-100)', fg: 'var(--ink-700)', icon: 'edit_note' },
  active: { bg: 'var(--success-bg)', fg: 'var(--success-text)', icon: 'play_circle' },
  paused: { bg: 'var(--warning-bg)', fg: 'var(--warning-text)', icon: 'pause_circle' },
  expired: { bg: 'var(--surface-100)', fg: 'var(--ink-700)', icon: 'event_busy' },
  exhausted: { bg: 'var(--error-bg)', fg: 'var(--error-text)', icon: 'block' },
};

const StatusPill: React.FC<{ status: CouponStatus }> = ({ status }) => {
  const s = STATUS_STYLE[status];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)]"
      style={{ background: s.bg, color: s.fg }}
    >
      <Icon name={s.icon} size={14} filled />
      {COUPON_STATUS_LABEL[status]}
    </span>
  );
};

/** ตัวอย่างโค้ดในชุด — สุ่มจากชุดตัวอักษรที่ตัดตัวที่อ่านผิดง่ายออกแล้ว */
const sampleCode = (prefix: string, len: number): string => {
  let out = '';
  for (let i = 0; i < len; i += 1) {
    out += BATCH_DEFAULTS.alphabet[Math.floor(Math.random() * BATCH_DEFAULTS.alphabet.length)];
  }
  return prefix ? `${prefix}-${out}` : out;
};

export interface CouponScreenProps {
  initialState?: ScreenState;
  /** คูปองจากชั้นข้อมูล — ไม่ส่งมา = ใช้ข้อมูลจำลอง */
  coupons?: Coupon[];
  /** บันทึกจริง — ไม่ส่งมา = แก้ในเครื่องอย่างเดียว (โหมดสาธิต) */
  onSave?: (c: Coupon) => Promise<void>;
  onRefresh?: () => void;
  refreshing?: boolean;
  /**
   * id ของคูปองที่แก้ไม่ได้ พร้อมเหตุผล
   *
   * ใช้กับคูปองชนิดที่ backend มีแต่หน้าจอนี้ยังไม่รองรับ (แถมของฟรี · ให้คะแนน)
   * แสดงในตารางได้ แต่กดแก้ไม่ได้ — ซ่อนไปเลยจะทำให้คนตั้งค่าไม่รู้ว่ามีอยู่
   */
  lockedIds?: Map<string, string>;
  /**
   * แสดงช่องอัปโหลดภาพคูปองหรือไม่
   *
   * ⚠️ endpoint รายการของคูปอง **ไม่ส่งฟิลด์รูปกลับมา** (ต่างจากของรางวัลและสิทธิพิเศษ)
   *    อัปโหลดขึ้นไปแล้วไฟล์ถูกเก็บจริง แต่พอโหลดรายการใหม่รูปจะหายจากหน้าจอ
   *    ซึ่งอ่านเหมือนอัปโหลดไม่สำเร็จ จึงปิดไว้ก่อนจนกว่า backend จะส่งฟิลด์นี้มาด้วย
   */
  showImage?: boolean;
}

export const CouponScreen: React.FC<CouponScreenProps> = ({
  initialState = 'ready',
  coupons,
  onSave,
  onRefresh,
  refreshing = false,
  lockedIds,
  showImage = true,
}) => {
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [rows, setRows] = useState<Coupon[]>(coupons ?? COUPONS);

  /* ข้อมูลจาก API มาทีหลัง — ต้องรับทั้งค่าและสถานะหน้าจอ
     ถ้าลืมอัปเดต screenState หน้าจะค้างที่ "กำลังโหลด" ตลอดไป */
  useEffect(() => { if (coupons) setRows(coupons); }, [coupons]);
  useEffect(() => { setScreenState(initialState); }, [initialState]);
  const [density, setDensity] = useTableDensity();

  const [query, setQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | CouponKind>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | CouponStatus>('all');
  const [stackFilter, setStackFilter] = useState<'all' | StackRule>('all');
  const [sort, setSort] = useState<SortState | null>({ columnId: 'expiresAt', direction: 'asc' });
  const [visibleCols, setVisibleCols] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [edit, setEdit] = useState<Coupon | null>(null);
  const [confirmSave, setConfirmSave] = useState<Coupon | null>(null);

  /* การสร้างโค้ดแบบชุด */
  const [batchOpen, setBatchOpen] = useState(false);
  const [batch, setBatch] = useState({ ...BATCH_DEFAULTS, name: '', kind: 'amount' as CouponKind, value: 100, minSpend: 500, expiresAt: '2026-12-31' });
  const [batchPreview, setBatchPreview] = useState<string[]>([]);
  const [confirmBatch, setConfirmBatch] = useState(false);

  const regenPreview = (prefix: string, len: number) =>
    setBatchPreview(Array.from({ length: 5 }, () => sampleCode(prefix, len)));

  /* ---------------------------------------------------------------- ตัวกรอง */

  const filtered = useMemo(() => rows.filter((c) => {
    if (kindFilter !== 'all' && c.kind !== kindFilter) return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (stackFilter !== 'all' && c.stackRule !== stackFilter) return false;
    if (query) {
      const q = query.trim().toLowerCase();
      if (!c.code.toLowerCase().includes(q) && !c.name.includes(query.trim())) return false;
    }
    return true;
  }), [rows, kindFilter, statusFilter, stackFilter, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (c: Coupon): string | number => {
      switch (sort.columnId) {
        case 'value': return c.value ?? 0;
        case 'minSpend': return c.minSpend;
        case 'expiresAt': return c.expiresAt;
        case 'quota': return c.totalQuota === null ? Number.MAX_SAFE_INTEGER : c.totalQuota - c.usedCount;
        case 'usedCount': return c.usedCount;
        default: return c.code;
      }
    };
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filtered, sort]);

  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const columns: Array<Column<Coupon>> = useMemo(() => [
    {
      id: 'code', header: 'โค้ด', width: 208, pinned: true, required: true, sortable: true,
      value: (c) => c.code,
      cell: (c) => (
        <span className="flex flex-col leading-[1.3]">
          <span className="bo-num bo-truncate font-[var(--weight-semibold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{c.batch ? `${c.batch.prefix}-••••••` : c.code}</span>
          <span className="bo-truncate text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{c.name}</span>
        </span>
      ),
    },
    {
      id: 'kind', header: 'ประเภท', width: 152,
      value: (c) => COUPON_KIND_LABEL[c.kind],
      cell: (c) => (
        <span className="inline-flex items-center gap-[var(--space-15)]">
          <Icon
            name={c.kind === 'freeship' ? 'local_shipping' : c.kind === 'percent' ? 'percent' : 'payments'}
            size={16}
            className="text-[var(--blue-700)]"
          />
          {COUPON_KIND_LABEL[c.kind]}
        </span>
      ),
    },
    { id: 'value', header: 'มูลค่า', width: 188, sortable: true, value: (c) => valueText(c), cell: (c) => <span className="bo-num font-[var(--weight-medium)]" style={{ textAlign: 'left' }}>{valueText(c)}</span> },
    { id: 'minSpend', header: 'ยอดขั้นต่ำ', width: 124, numeric: true, sortable: true, value: (c) => c.minSpend, cell: (c) => (c.minSpend === 0 ? <span className="text-[var(--ink-700)]">ไม่มี</span> : <span className="bo-num">{c.minSpend.toLocaleString('th-TH')}</span>) },
    {
      id: 'expiresAt', header: 'วันหมดอายุ', width: 148, sortable: true,
      value: (c) => c.expiresAt,
      cell: (c) => {
        const days = Math.round((new Date(c.expiresAt).getTime() - new Date('2026-08-22').getTime()) / 86_400_000);
        const soon = days >= 0 && days <= 14 && c.status === 'active';
        return (
          <span className="flex flex-col leading-[1.3]">
            <span className="bo-num" style={{ textAlign: 'left' }}>{formatThaiDate(c.expiresAt)}</span>
            {soon && (
              <span className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--warning-text)]">
                <Icon name="schedule" size={13} filled />เหลือ {days} วัน
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'quota', header: 'สิทธิ์คงเหลือ / ทั้งหมด', width: 196, numeric: true, sortable: true,
      value: (c) => (c.totalQuota === null ? '' : c.totalQuota - c.usedCount),
      cell: (c) => {
        if (c.totalQuota === null) return <span className="text-[var(--ink-700)]">ไม่จำกัด</span>;
        const left = c.totalQuota - c.usedCount;
        const pct = Math.round((c.usedCount / c.totalQuota) * 100);
        return (
          <span className="flex items-center justify-end gap-[var(--space-2)]">
            <span className="bo-num font-[var(--weight-medium)]" style={{ color: left === 0 ? 'var(--error-text)' : 'var(--ink-900)' }}>
              {left.toLocaleString('th-TH')}
            </span>
            <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">/ {c.totalQuota.toLocaleString('th-TH')}</span>
            <span
              role="img"
              aria-label={`ใช้ไป ${pct}%`}
              className="h-[8px] w-[56px] overflow-hidden rounded-[var(--radius-pill)] bg-[var(--surface-100)]"
            >
              <span className="block h-full" style={{ width: `${pct}%`, background: left === 0 ? 'var(--error-solid)' : 'var(--blue-700)' }} />
            </span>
          </span>
        );
      },
    },
    { id: 'perPerson', header: 'สิทธิ์ต่อคน', width: 108, numeric: true, value: (c) => c.perPerson, cell: (c) => <span className="bo-num">{c.perPerson.toLocaleString('th-TH')}</span> },
    { id: 'usedCount', header: 'ใช้ไปแล้ว', width: 116, numeric: true, sortable: true, value: (c) => c.usedCount, cell: (c) => <span className="bo-num">{c.usedCount.toLocaleString('th-TH')}</span> },
    {
      id: 'stackRule', header: 'ใช้ร่วมกับส่วนลดอื่น', width: 236,
      value: (c) => STACK_RULE_LABEL[c.stackRule],
      cell: (c) => (
        <span className="inline-flex items-center gap-1">
          <Icon
            name={c.stackRule === 'exclusive' ? 'block' : 'layers'}
            size={15}
            className={c.stackRule === 'exclusive' ? 'text-[var(--ink-700)]' : 'text-[var(--blue-700)]'}
          />
          <span className="bo-truncate">{STACK_RULE_LABEL[c.stackRule]}</span>
        </span>
      ),
    },
    { id: 'status', header: 'สถานะ', width: 124, value: (c) => COUPON_STATUS_LABEL[c.status], cell: (c) => <StatusPill status={c.status} /> },
    {
      id: 'batch', header: 'ชุดโค้ด', width: 168,
      value: (c) => (c.batch ? `${c.batch.prefix} ${c.batch.issued}/${c.batch.size}` : 'โค้ดเดี่ยว'),
      cell: (c) => (c.batch
        ? (
          <span className="flex flex-col leading-[1.3] text-[length:var(--text-table-sm)]">
            <span className="bo-num text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{c.batch.prefix}-••••••</span>
            <span className="bo-num text-[var(--ink-700)]" style={{ textAlign: 'left' }}>
              ออกแล้ว {c.batch.issued.toLocaleString('th-TH')} / {c.batch.size.toLocaleString('th-TH')}
            </span>
          </span>
        )
        : <span className="text-[var(--ink-700)]">โค้ดเดี่ยว</span>),
    },
    { id: 'createdBy', header: 'สร้างโดย', width: 152, value: (c) => c.createdBy, cell: (c) => <span className="bo-truncate block">{c.createdBy}</span> },
    {
      id: 'actions', header: 'จัดการ', width: 92, align: 'right', value: () => '',
      cell: (c) => {
        /* คูปองชนิดที่หน้าจอนี้ยังไม่รองรับ — แสดงได้แต่แก้ไม่ได้
           บอกเหตุผลผ่าน title เพื่อไม่ให้ดูเหมือนปุ่มเสีย */
        const locked = lockedIds?.get(c.id);
        if (locked) {
          return (
            <span
              title={locked}
              className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--ink-600)]"
            >
              <Icon name="lock" size={14} />
              แก้ที่นี่ไม่ได้
            </span>
          );
        }
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEdit(c); }}
            className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          >
            แก้ไข
          </button>
        );
      },
    },
  ], [lockedIds]);

  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  const totals = useMemo(() => ({
    active: rows.filter((c) => c.status === 'active').length,
    used: rows.reduce((s, c) => s + c.usedCount, 0),
    exhausted: rows.filter((c) => c.status === 'exhausted').length,
    noCap: rows.filter((c) => c.kind === 'percent' && c.maxDiscount === null).length,
    stackable: rows.filter((c) => c.stackRule !== 'exclusive').length,
  }), [rows]);

  const stats: SummaryStat[] = [
    { label: 'คูปองที่ใช้งานอยู่', value: totals.active.toLocaleString('th-TH') + ' รายการ', icon: 'confirmation_number', tone: 'brand' },
    { label: 'ใช้ไปแล้วรวม', value: totals.used.toLocaleString('th-TH') + ' ครั้ง', icon: 'check_circle', tone: 'success' },
    { label: 'สิทธิ์หมดแล้ว', value: totals.exhausted.toLocaleString('th-TH') + ' รายการ', icon: 'block', tone: totals.exhausted ? 'error' : 'default' },
    {
      label: 'ใช้ร่วมกับส่วนลดอื่นได้', value: totals.stackable.toLocaleString('th-TH') + ' รายการ', icon: 'layers',
      tone: totals.stackable ? 'warning' : 'default',
      note: totals.stackable ? 'ยังไม่ได้กำหนดนโยบาย' : undefined,
    },
  ];

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (kindFilter !== 'all') out.push({ id: 'kind', field: 'ประเภท', display: COUPON_KIND_LABEL[kindFilter] });
    if (statusFilter !== 'all') out.push({ id: 'status', field: 'สถานะ', display: COUPON_STATUS_LABEL[statusFilter] });
    if (stackFilter !== 'all') out.push({ id: 'stack', field: 'ใช้ร่วมกับส่วนลดอื่น', display: STACK_RULE_LABEL[stackFilter] });
    return out;
  }, [kindFilter, statusFilter, stackFilter]);

  const clearAll = () => { setKindFilter('all'); setStatusFilter('all'); setStackFilter('all'); setQuery(''); setPage(1); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({
      columns, rows: sorted, columnIds: visible.filter((id) => id !== 'actions'),
      filename: exportFilename('คูปองและโค้ดส่วนลด'), format,
    });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการแล้ว` });
  };

  const save = (c: Coupon, reason?: string) => {
    const before = rows;
    setRows((prev) => (prev.some((x) => x.id === c.id)
      ? prev.map((x) => (x.id === c.id ? { ...c, provisional: false } : x))
      : [{ ...c, provisional: false }, ...prev]));
    setEdit(null);
    setConfirmSave(null);

    /* โหมดสาธิต — ไม่มีชั้น API จบที่ optimistic พร้อมปุ่มเลิกทำ */
    if (!onSave) {
      toast.show({
        tone: 'success',
        message: `บันทึกคูปอง ${c.code} แล้ว`,
        undo: { onUndo: () => setRows(before), seconds: 10 },
      });
      void reason;
      return;
    }

    /* ต่อ backend จริง — คืนค่าเดิมเมื่อบันทึกไม่สำเร็จ ไม่ปล่อยให้หน้าจอโกหก */
    void onSave(c)
      .then(() => toast.show({ tone: 'success', message: `บันทึกคูปอง ${c.code} แล้ว` }))
      .catch((err: unknown) => {
        setRows(before);
        toast.show({
          tone: 'error',
          message: err instanceof Error ? err.message : `บันทึกคูปอง ${c.code} ไม่สำเร็จ`,
        });
      });
    void reason;
  };

  const createBatch = (reason?: string) => {
    const before = rows;
    const created: Coupon = {
      id: `cp-${Date.now()}`,
      /* โค้ดที่เก็บคือคำนำหน้าล้วน — ส่วนสุ่มต่อท้ายระบบสร้างให้ทีละโค้ด
         ห้ามใส่ตัวปิดบังอย่าง "-••••••" ลงไป เพราะจะถูกส่งออกไปเป็นโค้ดจริง */
      code: batch.prefix,
      name: batch.name || 'ชุดโค้ดใหม่',
      kind: batch.kind,
      value: batch.kind === 'freeship' ? null : batch.value,
      maxDiscount: batch.kind === 'percent' ? 500 : null,
      minSpend: batch.minSpend,
      from: '2026-08-22',
      expiresAt: batch.expiresAt,
      totalQuota: batch.size,
      usedCount: 0,
      perPerson: 1,
      stackRule: 'exclusive',
      status: 'draft',
      batch: { prefix: batch.prefix, size: batch.size, issued: onSave ? 0 : batch.size, codeLength: batch.randomLength },
      createdBy: onSave ? '—' : 'สุดารัตน์ ทองดี',
      provisional: false,
    };
    setRows((prev) => [created, ...prev]);
    setBatchOpen(false);
    setConfirmBatch(false);
    const doneMessage = `สร้างโค้ด ${batch.size.toLocaleString('th-TH')} รายการในชุด ${batch.prefix} แล้ว — สถานะฉบับร่าง ต้องเปิดใช้งานก่อนแจก`;

    /* โหมดสาธิต — แก้ในเครื่องอย่างเดียว จึงเลิกทำได้ */
    if (!onSave) {
      toast.show({ tone: 'success', message: doneMessage, undo: { onUndo: () => setRows(before), seconds: 10 } });
      void reason;
      return;
    }

    /* ต่อ backend จริง — ไม่มีปุ่มเลิกทำ เพราะโค้ดถูกสร้างไปแล้ว ล้มเหลวให้คืนตารางกลับ */
    void onSave(created)
      .then(() => toast.show({ tone: 'success', message: doneMessage }))
      .catch((err: unknown) => {
        setRows(before);
        toast.show({
          tone: 'error',
          message: err instanceof Error ? err.message : `สร้างชุดโค้ด ${batch.prefix} ไม่สำเร็จ`,
        });
      });
    void reason;
  };

  const maxDiscountText = (c: Coupon): string | null => {
    if (c.kind !== 'percent' || c.value === null) return null;
    if (c.maxDiscount !== null) return `ส่วนลดสูงสุด ${c.maxDiscount.toLocaleString('th-TH')} บาท`;
    return 'ไม่มีเพดาน — ตะกร้า 80,000 บาทจะได้ส่วนลด ' + Math.round(80_000 * (c.value / 100)).toLocaleString('th-TH') + ' บาท';
  };

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="คูปองและโค้ดส่วนลด"
        count={sorted.length}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหาโค้ดหรือชื่อคูปอง" width={300} />}
        end={
          <>
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => setEdit({
                id: `cp-${Date.now()}`, code: '', name: '', kind: 'amount', value: 100,
                maxDiscount: null, minSpend: 500, from: '2026-09-01', expiresAt: '2026-12-31',
                totalQuota: 1_000, usedCount: 0, perPerson: 1, stackRule: 'exclusive',
                status: 'draft', batch: null, createdBy: 'สุดารัตน์ ทองดี', provisional: false,
              })}
            >
              <Icon name="add" size={17} />
              สร้างคูปอง
            </button>
            <button
              type="button"
              className={secondaryBtnCls}
              onClick={() => { setBatchOpen(true); regenPreview(batch.prefix, batch.randomLength); }}
            >
              <Icon name="dynamic_feed" size={17} />
              สร้างโค้ดแบบชุด
            </button>
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="coupons" value={visible} onChange={setVisibleCols} />
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
          </>
        }
      />

      {/* ⚠️ ระหว่างโหลดยังไม่มีตัวเลขจริง — ซ่อนดีกว่าแสดงค่าที่อ่านเหมือนของจริง */}
      {screenState !== 'loading' && <SummaryStrip stats={stats} />}

      {/* ⚠️ เก็บเฉพาะคำเตือนที่เป็นความเสี่ยงจริงของข้อมูล — ส่วนที่เล่าว่านโยบายยังไม่นิ่งถูกตัดออก
          คูปองเปอร์เซ็นต์ที่ไม่มีเพดานคือส่วนลดไม่จำกัด ถ้าไม่เตือนจะรู้ตัวตอนออกบิลแล้ว */}
      {totals.noCap > 0 && (
        <div className="border-b border-[var(--line-200)] px-[var(--space-4)] py-[var(--space-2)]">
          <Callout tone="error" title={`คูปองแบบเปอร์เซ็นต์ ${totals.noCap} รายการยังไม่ได้ตั้งเพดานส่วนลด`}>
            คูปองที่ไม่มีเพดานจะลดตามยอดซื้อโดยไม่จำกัด — ตั้งเพดานก่อนเปิดใช้งาน
          </Callout>
        </div>
      )}

      <FilterBar
        active={activeFilters}
        onRemove={(id) => {
          if (id === 'kind') setKindFilter('all');
          if (id === 'status') setStatusFilter('all');
          if (id === 'stack') setStackFilter('all');
          setPage(1);
        }}
        onClearAll={clearAll}
        currentFilters={{ kindFilter, statusFilter, stackFilter }}
        persistKey="coupons"
        resultCount={sorted.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ประเภท
          <select value={kindFilter} onChange={(e) => { setKindFilter(e.target.value as typeof kindFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            {(Object.keys(COUPON_KIND_LABEL) as CouponKind[]).map((k) => <option key={k} value={k}>{COUPON_KIND_LABEL[k]}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          สถานะ
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            {(Object.keys(COUPON_STATUS_LABEL) as CouponStatus[]).map((s) => <option key={s} value={s}>{COUPON_STATUS_LABEL[s]}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ใช้ร่วมกับส่วนลดอื่น
          <select value={stackFilter} onChange={(e) => { setStackFilter(e.target.value as typeof stackFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            {(Object.keys(STACK_RULE_LABEL) as StackRule[]).map((s) => <option key={s} value={s}>{STACK_RULE_LABEL[s]}</option>)}
          </select>
        </label>
      </FilterBar>

      <DataTable<Coupon>
        columns={columns.filter((c) => visible.includes(c.id))}
        rows={pageRows}
        rowKey={(c) => c.id}
        caption={`คูปองและโค้ดส่วนลด — ${sorted.length} รายการ`}
        state={screenState}
        rowCountHint={9}
        errorMessage="โหลดรายการคูปองไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
        onRetry={() => setScreenState('ready')}
        emptyState={activeFilters.length || query
          ? <EmptyState kind="no-results" title="ไม่พบคูปองที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
          : <EmptyState kind="no-data" title="ยังไม่มีคูปอง" description="สร้างคูปองเดี่ยว หรือสร้างโค้ดแบบชุดสำหรับแจกในงานอีเวนต์" />}
        sort={sort}
        onSortChange={setSort}
        onOpenRow={setEdit}
        density={density}
        persistKey="coupons"
        isFlagged={(c) => c.kind === 'percent' && c.maxDiscount === null}
        className="min-h-0 flex-1 rounded-none border-0"
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        totalItems={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      />

      {/* ------------------------------------------------------ แก้ไข / สร้าง */}
      <SideDrawer
        open={Boolean(edit)}
        onClose={() => setEdit(null)}
        title={rows.some((x) => x.id === edit?.id) ? 'แก้ไขคูปอง' : 'สร้างคูปอง'}
        subtitle={edit?.code || 'โค้ดเดี่ยว — ถ้าต้องการหลายพันโค้ดให้ใช้การสร้างแบบชุด'}
        width="lg"
        footer={edit && (
          <div className="flex flex-wrap items-center gap-[var(--space-2)]">
            <button
              type="button"
              onClick={() => setConfirmSave(edit)}
              disabled={!edit.code.trim() || !edit.name.trim() || (edit.kind === 'percent' && edit.maxDiscount === null)}
              className={primaryBtnCls}
            >
              บันทึก
            </button>
            <button type="button" onClick={() => setEdit(null)} className={secondaryBtnCls}>ยกเลิก</button>
            {edit.kind === 'percent' && edit.maxDiscount === null && (
              <span className="text-[length:var(--text-table-sm)] text-[var(--error-text)]">ส่วนลดแบบเปอร์เซ็นต์ต้องตั้งเพดานก่อน</span>
            )}
          </div>
        )}
      >
        {edit && (
          <div className="flex flex-col gap-[var(--space-4)]">
            {edit.usedCount > 0 && (
              <Callout tone="info" title={`คูปองนี้ถูกใช้ไปแล้ว ${edit.usedCount.toLocaleString('th-TH')} ครั้ง`}>
                การแก้มูลค่าหรือยอดขั้นต่ำมีผลกับการใช้ครั้งถัดไปเท่านั้น ไม่ย้อนหลัง —
                ถ้าต้องการเปลี่ยนเงื่อนไขจริง ควรปิดคูปองนี้แล้วสร้างโค้ดใหม่
                เพราะลูกค้าที่ถือโค้ดเดิมอ้างอิงเงื่อนไขที่ประกาศไว้ตอนแจก
              </Callout>
            )}

            <div className="grid grid-cols-2 gap-[var(--space-3)]">
              <FormRow label="โค้ด" required help="ตัวพิมพ์ใหญ่และตัวเลข ไม่เว้นวรรค">
                <input
                  data-autofocus
                  type="text"
                  value={edit.code}
                  onChange={(e) => setEdit({ ...edit, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                  className={`${inputCls} bo-num w-full`}
                  placeholder="WELCOME100"
                />
              </FormRow>
              <FormRow label="ชื่อที่ใช้เรียกภายใน" required help="ไม่แสดงกับลูกค้า — ใช้ค้นหาในหน้านี้">
                <input
                  type="text"
                  value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  className={`${inputCls} w-full`}
                  placeholder="ส่วนลดแรกเข้า 100 บาท"
                />
              </FormRow>
            </div>

            <FormRow label="ประเภทส่วนลด" required>
              <div className="flex flex-wrap gap-[var(--space-15)]">
                {(Object.keys(COUPON_KIND_LABEL) as CouponKind[]).map((k) => {
                  const on = edit.kind === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setEdit({
                        ...edit,
                        kind: k,
                        value: k === 'freeship' ? null : (edit.value ?? (k === 'percent' ? 10 : 100)),
                        maxDiscount: k === 'percent' ? (edit.maxDiscount ?? 500) : null,
                      })}
                      className={`min-h-[var(--hit-control)] rounded-[var(--radius-pill)] border px-[var(--space-3)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2 ${
                        on ? 'border-[var(--blue-700)] bg-[var(--blue-700)] font-[var(--weight-semibold)] text-white' : 'border-[var(--line-300)] text-[var(--ink-700)] hover:bg-[var(--surface-100)]'
                      }`}
                    >
                      {COUPON_KIND_LABEL[k]}
                    </button>
                  );
                })}
              </div>
            </FormRow>

            {edit.kind !== 'freeship' && (
              <div className="flex flex-wrap items-end gap-[var(--space-4)]">
                <FormRow label="มูลค่า" required>
                  <NumberField
                    value={edit.value ?? ''}
                    onChange={(v) => setEdit({ ...edit, value: v === '' ? null : v })}
                    unit={edit.kind === 'percent' ? '%' : 'บาท'}
                    min={1}
                    max={edit.kind === 'percent' ? 100 : undefined}
                    width={148}
                    ariaLabel="มูลค่าส่วนลด"
                  />
                </FormRow>
                {edit.kind === 'percent' && (
                  <FormRow
                    label="เพดานส่วนลด"
                    required
                    error={edit.maxDiscount === null ? 'ต้องตั้งเพดาน — ส่วนลดเปอร์เซ็นต์ที่ไม่มีเพดานทำให้ตะกร้าใหญ่ได้ส่วนลดเกินที่ตั้งใจ' : undefined}
                  >
                    <NumberField
                      value={edit.maxDiscount ?? ''}
                      onChange={(v) => setEdit({ ...edit, maxDiscount: v === '' ? null : v })}
                      unit="บาท" min={1} step={50} width={168} ariaLabel="เพดานส่วนลด"
                    />
                  </FormRow>
                )}
                {maxDiscountText(edit) && (
                  <p
                    className="m-0 flex-1 pb-[var(--space-15)] text-[length:var(--text-form)] leading-[var(--leading-thai)]"
                    style={{ color: edit.maxDiscount === null ? 'var(--error-text)' : 'var(--ink-700)' }}
                  >
                    <Icon name={edit.maxDiscount === null ? 'error' : 'info'} size={16} filled className="mr-1 inline align-[-3px]" />
                    {maxDiscountText(edit)}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-[var(--space-3)]">
              <FormRow label="ยอดขั้นต่ำที่ใช้ได้" help="0 = ไม่มียอดขั้นต่ำ">
                <NumberField value={edit.minSpend} onChange={(v) => setEdit({ ...edit, minSpend: v === '' ? 0 : v })} unit="บาท" min={0} step={100} width={168} ariaLabel="ยอดขั้นต่ำ" />
              </FormRow>
              <FormRow label="วันหมดอายุ" required>
                <input type="date" value={edit.expiresAt} onChange={(e) => setEdit({ ...edit, expiresAt: e.target.value })} className={`${inputCls} bo-num`} />
              </FormRow>
            </div>

            <div className="flex flex-wrap items-end gap-[var(--space-4)]">
              <FormRow label="จำนวนสิทธิ์ทั้งหมด" help="นับรวมทุกคน">
                <span className="flex items-center gap-[var(--space-2)]">
                  <NumberField
                    value={edit.totalQuota ?? ''}
                    onChange={(v) => setEdit({ ...edit, totalQuota: v === '' ? null : v })}
                    unit="สิทธิ์" min={1} step={100} width={168}
                    disabled={edit.totalQuota === null}
                    ariaLabel="จำนวนสิทธิ์ทั้งหมด"
                  />
                  <button
                    type="button"
                    onClick={() => setEdit({ ...edit, totalQuota: edit.totalQuota === null ? 1_000 : null })}
                    className={secondaryBtnCls}
                  >
                    {edit.totalQuota === null ? 'ตั้งเพดาน' : 'ไม่จำกัด'}
                  </button>
                </span>
              </FormRow>
              <FormRow label="จำนวนสิทธิ์ต่อคน" required>
                <NumberField value={edit.perPerson} onChange={(v) => setEdit({ ...edit, perPerson: v === '' ? 1 : v })} unit="ครั้ง" min={1} width={148} ariaLabel="สิทธิ์ต่อคน" />
              </FormRow>
              {edit.totalQuota !== null && edit.usedCount > 0 && (
                <Field label="คงเหลือ">
                  <span className="bo-num" style={{ textAlign: 'left' }}>
                    {(edit.totalQuota - edit.usedCount).toLocaleString('th-TH')} สิทธิ์
                  </span>
                </Field>
              )}
            </div>

            <FormRow
              label="เงื่อนไขการใช้ร่วมกับส่วนลดอื่น"
              required
              help="ยังไม่ได้กำหนดนโยบาย — ค่าที่ปลอดภัยที่สุดคือใช้ร่วมไม่ได้ เพราะเปิดให้ใช้ร่วมภายหลังง่ายกว่าการปิด"
            >
              <div className="flex flex-col gap-[var(--space-15)]">
                {(Object.keys(STACK_RULE_LABEL) as StackRule[]).map((s) => (
                  <label key={s} className="flex cursor-pointer items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] border border-[var(--line-200)] p-[var(--space-2)] hover:bg-[var(--blue-050)]">
                    <input
                      type="radio"
                      name="stackRule"
                      checked={edit.stackRule === s}
                      onChange={() => setEdit({ ...edit, stackRule: s })}
                      className="mt-[3px] h-4 w-4 shrink-0 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                    />
                    <span className="min-w-0">
                      <span className="block text-[length:var(--text-form-lg)] text-[var(--ink-900)]">{STACK_RULE_LABEL[s]}</span>
                      {s === 'all' && (
                        <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--warning-text)]">
                          ส่วนลดซ้อนกันหลายชั้นทำให้ยอดสุทธิต่ำกว่าที่คาดมาก — ต้องผ่านฝ่ายการเงินก่อน
                        </span>
                      )}
                      {s === 'exclusive' && (
                        <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                          ค่าตั้งต้น — ระบบจะเลือกส่วนลดที่ลูกค้าได้ประโยชน์สูงสุดให้อัตโนมัติ
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </FormRow>

            {showImage && (
            <ImageDropzone
              /* ⚠️ ต้องส่ง file กลับเข้าไปด้วย ไม่งั้นไฟล์หายตอนพิมพ์คำอธิบายภาพ */
              value={edit.imageUrl ? { url: edit.imageUrl, alt: edit.imageAlt ?? '', file: edit.imageFile } : null}
              onChange={(v: ImageValue | null) => setEdit({
                ...edit, imageUrl: v?.url ?? null, imageAlt: v?.alt ?? '', imageFile: v?.file,
              })}
              spec={IMAGE_SPEC}
              label="ภาพคูปอง"
              previewWidth={220}
            />
            )}

            <FormRow label="สถานะ" help="ฉบับร่างยังใช้ไม่ได้ · พักไว้ทำให้โค้ดที่แจกไปแล้วใช้ไม่ได้ชั่วคราว">
              <select
                value={edit.status}
                onChange={(e) => setEdit({ ...edit, status: e.target.value as CouponStatus })}
                className={inputCls}
                style={{ minWidth: 200 }}
              >
                {(Object.keys(COUPON_STATUS_LABEL) as CouponStatus[]).map((s) => (
                  <option key={s} value={s}>{COUPON_STATUS_LABEL[s]}</option>
                ))}
              </select>
            </FormRow>
          </div>
        )}
      </SideDrawer>

      {/* --------------------------------------------------- สร้างโค้ดแบบชุด */}
      <SideDrawer
        open={batchOpen}
        onClose={() => setBatchOpen(false)}
        title="สร้างโค้ดแบบชุด"
        subtitle="สร้างหลายพันโค้ดพร้อมกันสำหรับแจกในงานอีเวนต์หรือช่องทางออฟไลน์"
        width="lg"
        footer={
          <div className="flex flex-wrap items-center gap-[var(--space-2)]">
            <button
              type="button"
              onClick={() => setConfirmBatch(true)}
              disabled={!batch.prefix.trim() || batch.size < 1 || batch.size > BATCH_DEFAULTS.maxSize}
              className={primaryBtnCls}
            >
              สร้าง {batch.size.toLocaleString('th-TH')} โค้ด
            </button>
            <button type="button" onClick={() => setBatchOpen(false)} className={secondaryBtnCls}>ยกเลิก</button>
          </div>
        }
      >
        <div className="flex flex-col gap-[var(--space-4)]">
          <Callout tone="warning" title="โค้ดที่สร้างแล้วแจกออกไปแล้วเรียกคืนไม่ได้">
            ตรวจจำนวน คำนำหน้า และวันหมดอายุให้ครบก่อนกดสร้าง — ชุดที่สร้างจะอยู่ในสถานะฉบับร่าง
            ต้องเปิดใช้งานอีกครั้งก่อนโค้ดจะใช้ได้จริง
          </Callout>

          <div className="grid grid-cols-2 gap-[var(--space-3)]">
            <FormRow label="ชื่อชุด" help="ใช้ค้นหาและอ้างอิงในรายงาน">
              <input
                type="text"
                value={batch.name}
                onChange={(e) => setBatch({ ...batch, name: e.target.value })}
                className={`${inputCls} w-full`}
                placeholder="งาน PTT Health Fair 2026"
              />
            </FormRow>
            <FormRow label="คำนำหน้าโค้ด" required help="ตัวพิมพ์ใหญ่ ไม่เกิน 12 ตัว — ใช้แยกว่าโค้ดมาจากช่องทางไหน">
              <input
                type="text"
                value={batch.prefix}
                maxLength={12}
                onChange={(e) => {
                  const prefix = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setBatch({ ...batch, prefix });
                  regenPreview(prefix, batch.randomLength);
                }}
                className={`${inputCls} bo-num w-full`}
                placeholder="PTTFAIR26"
              />
            </FormRow>
          </div>

          <div className="flex flex-wrap items-end gap-[var(--space-4)]">
            <FormRow
              label="จำนวนโค้ด"
              required
              error={batch.size > BATCH_DEFAULTS.maxSize ? `สร้างได้ไม่เกิน ${BATCH_DEFAULTS.maxSize.toLocaleString('th-TH')} โค้ดต่อครั้ง` : undefined}
            >
              <NumberField
                value={batch.size}
                onChange={(v) => setBatch({ ...batch, size: v === '' ? 0 : v })}
                unit="โค้ด" min={1} step={100} width={180} ariaLabel="จำนวนโค้ดที่จะสร้าง"
              />
            </FormRow>
            <FormRow label="ความยาวส่วนสุ่ม" help="ยาวขึ้น = เดายากขึ้น แต่พิมพ์ยากขึ้น">
              <NumberField
                value={batch.randomLength}
                onChange={(v) => {
                  const len = v === '' ? 6 : Math.min(12, Math.max(4, v));
                  setBatch({ ...batch, randomLength: len });
                  regenPreview(batch.prefix, len);
                }}
                unit="ตัวอักษร" min={4} max={12} width={180} ariaLabel="ความยาวส่วนสุ่ม"
              />
            </FormRow>
            <button type="button" onClick={() => regenPreview(batch.prefix, batch.randomLength)} className={ghostBtnCls}>
              <Icon name="refresh" size={15} />
              สุ่มตัวอย่างใหม่
            </button>
          </div>

          <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-100)] p-[var(--space-3)]">
            <h3 className="m-0 text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">ตัวอย่างโค้ดที่จะได้</h3>
            <ul className="m-0 flex list-none flex-wrap gap-[var(--space-2)] p-0">
              {batchPreview.map((c, i) => (
                <li key={`${c}-${i}`} className="bo-num rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] py-[var(--space-1)] text-[length:var(--text-form-lg)] text-[var(--ink-900)]">
                  {c}
                </li>
              ))}
            </ul>
            <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
              ชุดตัวอักษรที่ใช้ตัด 0 O 1 I L ออกแล้ว เพราะคนอ่านผิดเวลาพิมพ์โค้ดจากกระดาษ —
              จำนวนโค้ดที่เป็นไปได้ทั้งหมด{' '}
              <strong className="bo-num">
                {Math.pow(BATCH_DEFAULTS.alphabet.length, batch.randomLength).toExponential(2)}
              </strong>{' '}
              ชุด ซึ่งเพียงพอกับ {batch.size.toLocaleString('th-TH')} โค้ดโดยไม่ต้องกังวลว่าจะซ้ำ
            </p>
          </section>

          <div className="grid grid-cols-2 gap-[var(--space-3)]">
            <FormRow label="ประเภทส่วนลดของทั้งชุด">
              <select
                value={batch.kind}
                onChange={(e) => setBatch({ ...batch, kind: e.target.value as CouponKind })}
                className={inputCls}
              >
                {(Object.keys(COUPON_KIND_LABEL) as CouponKind[]).map((k) => <option key={k} value={k}>{COUPON_KIND_LABEL[k]}</option>)}
              </select>
            </FormRow>
            {batch.kind !== 'freeship' && (
              <FormRow label="มูลค่า">
                <NumberField
                  value={batch.value}
                  onChange={(v) => setBatch({ ...batch, value: v === '' ? 0 : v })}
                  unit={batch.kind === 'percent' ? '%' : 'บาท'}
                  min={1}
                  width={148}
                  ariaLabel="มูลค่าส่วนลดของทั้งชุด"
                />
              </FormRow>
            )}
            <FormRow label="ยอดขั้นต่ำ">
              <NumberField value={batch.minSpend} onChange={(v) => setBatch({ ...batch, minSpend: v === '' ? 0 : v })} unit="บาท" min={0} step={100} width={168} ariaLabel="ยอดขั้นต่ำของทั้งชุด" />
            </FormRow>
            <FormRow label="วันหมดอายุ">
              <input type="date" value={batch.expiresAt} onChange={(e) => setBatch({ ...batch, expiresAt: e.target.value })} className={`${inputCls} bo-num`} />
            </FormRow>
          </div>

          <Callout tone="info">
            ทุกโค้ดในชุดใช้ได้ 1 ครั้งต่อคน และใช้ร่วมกับส่วนลดอื่นไม่ได้ —
            ถ้าต้องการเงื่อนไขอื่น ให้แก้ที่รายการชุดหลังสร้างเสร็จ
            ไฟล์รายการโค้ดทั้งชุดดาวน์โหลดได้จากหน้ารายละเอียดของชุดหลังสร้าง
          </Callout>
        </div>
      </SideDrawer>

      <ConfirmDialog
        open={Boolean(confirmSave)}
        tone={confirmSave?.status === 'active' ? 'danger' : 'default'}
        title="บันทึกคูปอง"
        description={confirmSave ? (
          <>
            <p className="m-0 mb-[var(--space-2)]">
              <strong className="bo-num">{confirmSave.code}</strong> · {valueText(confirmSave)} ·
              ยอดขั้นต่ำ {confirmSave.minSpend.toLocaleString('th-TH')} บาท ·
              หมดอายุ {formatThaiDate(confirmSave.expiresAt)}
            </p>
            <p className="m-0">{STACK_RULE_LABEL[confirmSave.stackRule]}</p>
            {confirmSave.status === 'active' && (
              <p className="m-0 mt-[var(--space-2)] font-[var(--weight-medium)] text-[var(--ink-900)]">
                สถานะ &quot;ใช้งานอยู่&quot; — โค้ดนี้ใช้ได้ทันทีที่บันทึก
              </p>
            )}
          </>
        ) : ''}
        confirmLabel="บันทึก"
        /* ระบบคูปอง (BIL) ยังไม่เก็บประวัติการแก้ไข — ต่อระบบจริงแล้วไม่ขอเหตุผลที่จะถูกทิ้ง */
        requireReason={!onSave}
        reasonLabel="เหตุผลที่แก้"
        reasonPresets={[
          'สร้างตามแผนแคมเปญที่อนุมัติแล้ว',
          'ตั้งเพดานส่วนลดตามที่ฝ่ายการเงินกำหนด',
          'ปิดโค้ดเพราะพบการใช้ผิดวัตถุประสงค์',
        ]}
        onConfirm={(reason) => confirmSave && save(confirmSave, reason)}
        onCancel={() => setConfirmSave(null)}
      />

      <ConfirmDialog
        open={confirmBatch}
        tone="danger"
        title={`สร้างโค้ด ${batch.size.toLocaleString('th-TH')} รายการ`}
        description={
          <>
            <p className="m-0 mb-[var(--space-2)]">
              คำนำหน้า <strong className="bo-num">{batch.prefix || '(ยังไม่กรอก)'}</strong> ·{' '}
              {batch.kind === 'freeship' ? 'ฟรีค่าจัดส่ง' : `${batch.value.toLocaleString('th-TH')}${batch.kind === 'percent' ? '%' : ' บาท'}`} ·
              ยอดขั้นต่ำ {batch.minSpend.toLocaleString('th-TH')} บาท ·
              หมดอายุ {formatThaiDate(batch.expiresAt)}
            </p>
            <p className="m-0 font-[var(--weight-medium)] text-[var(--error-text)]">
              โค้ดที่สร้างแล้วลบไม่ได้ ทำได้แค่ปิดการใช้งาน — ถ้าโค้ดถูกแจกออกไปแล้วจะเรียกคืนไม่ได้
            </p>
          </>
        }
        confirmLabel="สร้างเป็นฉบับร่าง"
        requireReason={!onSave}
        reasonLabel="สร้างชุดนี้เพื่ออะไร"
        reasonPresets={[
          'แจกในงานอีเวนต์ตามแผนการตลาด',
          'แจกผ่าน LINE OA ในแคมเปญเดือนนี้',
          'สำรองไว้ให้ทีมขายใช้กับลูกค้าองค์กร',
        ]}
        onConfirm={createBatch}
        onCancel={() => setConfirmBatch(false)}
      />
    </div>
  );
};

export default CouponScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-20   จัดการคูปองส่วนลด — ประเภท (จำนวนเงิน/เปอร์เซ็นต์/ฟรีค่าส่ง) · ยอดขั้นต่ำ ·
               วันหมดอายุ · จำนวนสิทธิ์ทั้งหมด · จำนวนสิทธิ์ต่อคน · เงื่อนไขการใช้ร่วมกับส่วนลดอื่น
   FR-ADM-21   สร้างโค้ดส่วนลดแบบเดี่ยวและแบบชุด (Bulk Code Generation)
   FR-ADM-19   สร้าง/แก้ไข/หยุดแคมเปญได้เองโดยไม่ต้องเขียนโปรแกรม
   FR-RPT-07   รายงานการใช้คูปอง
   FR-ADM-37   ทุกการแก้ไขและการสร้างชุดโค้ดเข้า Audit Log พร้อมเหตุผล
   BR-16       ลำดับการแสดงราคา — คูปองที่ใช้ร่วมกับส่วนลดอื่นไม่ได้ต้องเลือกที่ลูกค้าได้ประโยชน์สูงสุด

   ประเด็นค้าง (⛔):
   — คำถามที่ยังค้างกับทีม backend เงื่อนไขการใช้ส่วนลดร่วมกัน (คะแนน + คูปอง + ส่วนลดตามระดับ) ยังไม่มีคำตอบ
     ค่าตั้งต้นของทุกคูปองในระบบจึงเป็น "ใช้ร่วมไม่ได้" ซึ่งเป็นค่าที่ปลอดภัยแต่ไม่ใช่การตัดสินใจ
   — เมื่อลูกค้าคืนสินค้าที่ใช้คูปอง คูปองควรคืนให้ใช้ได้อีกครั้งหรือถือว่าใช้แล้ว (เกี่ยวกับ BR-14)
   — ยังไม่มีการจำกัดคูปองตามกลุ่มลูกค้าหรือระดับสมาชิกในหน้านี้
     (FR-ADM-19 ระบุ Condition ไว้กว้างกว่านี้ — ต้องตกลงว่าคูปองใช้กลไกเดียวกับแคมเปญหรือแยก)
   — การดาวน์โหลดไฟล์รายการโค้ดทั้งชุดต้องมี endpoint แยก และต้องบันทึกว่าใครดาวน์โหลด
     เพราะไฟล์นั้นคือโค้ดที่ใช้เงินได้ (อยู่ใน ADMIN-API-REQUIREMENTS)
   ============================================================================ */
